import { useState, useEffect, useCallback } from 'react';
import { Storage } from '../storage';
import { TIMELINE_CATEGORIES } from '../constants';
import Icon from '../components/Icon';
import { TplThumb } from '../components/TemplateBrowser';
import ColorCustomizer from '../components/ColorCustomizer';
import MobileMenu from '../components/MobileMenu';
import useIsMobile from '../hooks/useIsMobile';
import RichTextEditor from '../components/RichTextEditor';

// ─── ADMIN PANEL ──────────────────────────────────────────────────────────────
//
//  Four-tab interface: Stats · Users · Content · Moderation Log
//  Only reachable when user.isAdmin = true (App.jsx redirects non-admins).
//

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) => (n ?? 0).toLocaleString();

const dateStr = (ms) => ms
  ? new Date(ms).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
  : '—';

const Avatar = ({ user, size = 32 }) => user?.avatar
  ? <img src={user.avatar} alt="" style={{ width: size, height: size, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
  : (
    <div style={{
      width: size, height: size, borderRadius: '50%', flexShrink: 0,
      background: 'linear-gradient(135deg, var(--accent), var(--accent3))',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: size * 0.35, fontWeight: 700, color: 'var(--on-accent)',
    }}>
      {(user?.name || '?').trim().split(/\s+/).map(w => w[0]).slice(0, 2).join('').toUpperCase()}
    </div>
  );

const Badge = ({ children, color = 'var(--accent)' }) => (
  <span style={{
    display: 'inline-block', padding: '2px 8px', borderRadius: 20,
    fontSize: 11, fontWeight: 600, background: color + '22', color,
    lineHeight: 1.6,
  }}>
    {children}
  </span>
);

const StatCard = ({ label, value, sub }) => (
  <div style={{
    background: 'var(--surface)', border: '1px solid var(--border)',
    borderRadius: 'var(--radius)', padding: '20px 24px', flex: '1 1 160px',
  }}>
    <div style={{ fontSize: 28, fontFamily: 'var(--font-display)', fontWeight: 700, color: 'var(--accent)' }}>{fmt(value)}</div>
    <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>{label}</div>
    {sub != null && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>{sub}</div>}
  </div>
);

// ── Toast ─────────────────────────────────────────────────────────────────────

const Toast = ({ toasts, remove }) => (
  <div style={{ position: 'fixed', bottom: 24, right: 24, display: 'flex', flexDirection: 'column', gap: 8, zIndex: 10000 }}>
    {toasts.map(t => (
      <div key={t.id} style={{
        background: t.type === 'error' ? 'var(--danger)' : 'var(--success, #22c55e)',
        color: '#fff', padding: '10px 16px', borderRadius: 'var(--radius)',
        fontSize: 13, display: 'flex', gap: 10, alignItems: 'center',
        boxShadow: '0 4px 12px rgba(0,0,0,.25)', minWidth: 240, maxWidth: 380,
      }}>
        <span style={{ flex: 1 }}>{t.msg}</span>
        <button onClick={() => remove(t.id)} style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', opacity: 0.7 }}>✕</button>
      </div>
    ))}
  </div>
);

function useToasts() {
  const [toasts, setToasts] = useState([]);
  const add  = useCallback((msg, type = 'success') => {
    const id = Date.now();
    setToasts(t => [...t, { id, msg, type }]);
    setTimeout(() => setToasts(t => t.filter(x => x.id !== id)), 4000);
  }, []);
  const remove = useCallback((id) => setToasts(t => t.filter(x => x.id !== id)), []);
  return { toasts, add, remove };
}

// ── Confirm modal ─────────────────────────────────────────────────────────────

const ConfirmModal = ({ open, title, body, confirmLabel = 'Confirm', danger = true, onConfirm, onClose, requireReason = false, loading }) => {
  const [reason, setReason] = useState('');

  useEffect(() => { if (!open) setReason(''); }, [open]);

  if (!open) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 28, width: '100%', maxWidth: 440 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 12 }}>{title}</h2>
        {body && <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: requireReason ? 16 : 24, lineHeight: 1.6 }}>{body}</p>}
        {requireReason && (
          <div style={{ marginBottom: 20 }}>
            <label htmlFor="adm-reason" style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Reason <span style={{ color: 'var(--danger)' }}>*</span></label>
            <textarea
              id="adm-reason"
              value={reason}
              onChange={e => setReason(e.target.value)}
              rows={3}
              placeholder="Describe the reason for this action…"
              style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }}
            />
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button
            className="btn-primary"
            onClick={() => onConfirm(reason)}
            disabled={loading || (requireReason && !reason.trim())}
            style={{ background: danger ? 'var(--danger)' : undefined, opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Please wait…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── User detail modal ─────────────────────────────────────────────────────────

const UserDetailModal = ({ userId, onClose, onBanToggle }) => {
  const [data, setData]   = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr]     = useState('');

  useEffect(() => {
    if (!userId) return;
    setLoading(true);
    Storage.adminGetUser(userId)
      .then(d => { setData(d); setLoading(false); })
      .catch(e => { setErr(e.message); setLoading(false); });
  }, [userId]);

  if (!userId) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 8500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{
        background: 'var(--surface)', border: '1px solid var(--border)',
        borderRadius: 'var(--radius)', width: '100%', maxWidth: 600,
        maxHeight: '85vh', overflowY: 'auto', padding: 28,
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20 }}>User Details</h2>
          <button className="btn-ghost btn-sm" onClick={onClose}><Icon name="close" size={14} /></button>
        </div>

        {loading && <p style={{ color: 'var(--text2)' }}>Loading…</p>}
        {err    && <p style={{ color: 'var(--danger)' }}>{err}</p>}

        {data && (() => {
          const u = data.user;
          return (
            <>
              <div style={{ display: 'flex', gap: 16, alignItems: 'center', marginBottom: 20 }}>
                <Avatar user={u} size={52} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: 16 }}>{u.name}</div>
                  <div style={{ color: 'var(--text3)', fontSize: 13 }}>{u.email}</div>
                  <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                    {u.isAdmin  && <Badge color="var(--accent3)">Admin</Badge>}
                    {u.isBanned && <Badge color="var(--danger)">Banned</Badge>}
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>JOINED</div>
                  <div style={{ fontSize: 13 }}>{dateStr(u.createdAt)}</div>
                </div>
                <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12 }}>
                  <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4 }}>TIMELINES</div>
                  <div style={{ fontSize: 13 }}>{data.timelines?.length ?? 0}</div>
                </div>
                {u.isBanned && (
                  <div style={{ background: 'var(--bg2)', borderRadius: 8, padding: 12, gridColumn: '1 / -1' }}>
                    <div style={{ fontSize: 11, color: 'var(--danger)', marginBottom: 4 }}>BAN REASON</div>
                    <div style={{ fontSize: 13 }}>{u.banReason || '—'}</div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Banned {dateStr(u.bannedAt)}</div>
                  </div>
                )}
              </div>

              {data.timelines?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Timelines</div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {data.timelines.slice(0, 10).map(t => (
                      <div key={t.id} style={{ display: 'flex', gap: 10, alignItems: 'center', fontSize: 13, padding: '6px 10px', background: 'var(--bg2)', borderRadius: 6 }}>
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</span>
                        <Badge color={t.privacy === 'public' ? 'var(--accent)' : 'var(--text3)'}>{t.privacy}</Badge>
                        <span style={{ color: 'var(--text3)', fontSize: 11 }}>{dateStr(t.createdAt)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {data.recentComments?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Recent Comments</div>
                  {data.recentComments.slice(0, 5).map(c => (
                    <div key={c.id} style={{ fontSize: 13, padding: '8px 10px', background: 'var(--bg2)', borderRadius: 6, marginBottom: 4 }}>
                      <div style={{ marginBottom: 2 }}>{c.text}</div>
                      <div style={{ fontSize: 11, color: 'var(--text3)' }}>{dateStr(c.createdAt)} on "{c.timelineTitle}"</div>
                    </div>
                  ))}
                </div>
              )}

              {data.moderationHistory?.length > 0 && (
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text2)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Moderation History</div>
                  {data.moderationHistory.map(m => (
                    <div key={m.id} style={{ fontSize: 13, padding: '6px 10px', background: 'var(--bg2)', borderRadius: 6, marginBottom: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
                      <Badge color="var(--accent)">{m.action}</Badge>
                      <span style={{ flex: 1, color: 'var(--text2)' }}>{m.reason || '—'}</span>
                      <span style={{ color: 'var(--text3)', fontSize: 11 }}>{dateStr(m.createdAt)}</span>
                    </div>
                  ))}
                </div>
              )}

              {!u.isAdmin && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: 8, borderTop: '1px solid var(--border)' }}>
                  <button
                    className="btn-primary"
                    style={{ background: u.isBanned ? undefined : 'var(--danger)', display: 'flex', alignItems: 'center', gap: 6 }}
                    onClick={() => onBanToggle(u)}
                  >
                    <Icon name="shield" size={14} />
                    {u.isBanned ? 'Unban User' : 'Ban User'}
                  </button>
                </div>
              )}
            </>
          );
        })()}
      </div>
    </div>
  );
};

// ── Stats Tab ─────────────────────────────────────────────────────────────────

const StatsTab = () => {
  const [stats,      setStats]      = useState(null);
  const [backup,     setBackup]     = useState(null);
  const [activity,   setActivity]   = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [err,        setErr]        = useState('');
  const [lastFetch,     setLastFetch]     = useState(null);
  const [secAgo,        setSecAgo]        = useState(0);
  const [backupRunning, setBackupRunning] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [s, b, a] = await Promise.all([
        Storage.adminGetStats(),
        Storage.getBackupStatus().catch(() => null),
        Storage.adminGetActivity().catch(() => []),
      ]);
      setStats(s);
      setBackup(b);
      setActivity(a);
      setLastFetch(Date.now());
      setLoading(false);
    } catch (e) {
      setErr(e.message);
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAll();
    const timer = setInterval(fetchAll, 60000);
    return () => clearInterval(timer);
  }, [fetchAll]);

  // Update "X seconds ago" counter every second
  useEffect(() => {
    if (!lastFetch) return;
    const t = setInterval(() => setSecAgo(Math.floor((Date.now() - lastFetch) / 1000)), 1000);
    return () => clearInterval(t);
  }, [lastFetch]);

  const formatBytes = (b) => {
    if (!b) return '—';
    if (b < 1048576) return `${(b / 1024).toFixed(1)} KB`;
    return `${(b / 1048576).toFixed(2)} MB`;
  };

  const ACTION_COLORS = {
    ban_user: 'var(--danger)', unban_user: 'var(--accent)',
    delete_timeline: 'var(--danger)', delete_comment: '#f97316',
    warn_user: '#eab308', bulk_ban: 'var(--danger)',
    bulk_warn: '#eab308', bulk_delete: 'var(--danger)',
    ip_ban: 'var(--danger)', ip_unban: 'var(--accent)',
  };

  if (loading) return <p style={{ color: 'var(--text2)', padding: 24 }}>Loading statistics…</p>;
  if (err)     return <p style={{ color: 'var(--danger)', padding: 24 }}>{err}</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20 }}>Platform Overview</h2>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          {lastFetch && <span style={{ fontSize: 12, color: 'var(--text3)' }}>Last updated {secAgo}s ago</span>}
          <button className="btn-ghost btn-sm" onClick={fetchAll}>Refresh</button>
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 32 }}>
        <StatCard label="Total Users"        value={stats.totalUsers}       sub={`${stats.newUsersToday} today`} />
        <StatCard label="Timelines"          value={stats.totalTimelines}   sub={`${stats.publicTimelines} public`} />
        <StatCard label="Comments"           value={stats.totalComments}    />
        <StatCard label="Likes"              value={stats.totalLikes}       />
        <StatCard label="Banned Users"       value={stats.bannedUsers}      />
        <StatCard label="Admin Users"        value={stats.adminUsers}       />
        <StatCard label="New Timelines (7d)" value={stats.newTimelines7d}   />
        <StatCard label="Moderation Actions" value={stats.moderationActions} />
      </div>

      {/* ── Backup Status ── */}
      {backup && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px 24px', marginBottom: 24 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16 }}>Database Backup</h3>
            <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
              {(backup.inProgress || backupRunning) && <Badge color="var(--accent)">Running…</Badge>}
              <button
                className="btn-ghost btn-sm"
                disabled={backup.inProgress || backupRunning}
                onClick={async () => {
                  setBackupRunning(true);
                  try {
                    await Storage.triggerManualBackup();
                    toast('Backup started. Refresh in a moment to see status.');
                    setTimeout(fetchAll, 5000);
                  } catch (e) { toast(e.message, 'error'); }
                  setBackupRunning(false);
                }}
                style={{ fontSize: 12 }}
              >
                {backupRunning ? 'Starting…' : 'Backup Now'}
              </button>
            </div>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, fontSize: 13 }}>
            <div><span style={{ color: 'var(--text3)', marginRight: 6 }}>Last backup:</span>{backup.lastBackup ? new Date(backup.lastBackup).toLocaleString() : 'Never'}</div>
            <div><span style={{ color: 'var(--text3)', marginRight: 6 }}>Last size:</span>{formatBytes(backup.lastSize)}</div>
            <div><span style={{ color: 'var(--text3)', marginRight: 6 }}>Files stored:</span>{backup.fileCount}</div>
            <div><span style={{ color: 'var(--text3)', marginRight: 6 }}>Total storage:</span>{formatBytes(backup.totalSize)}</div>
          </div>
        </div>
      )}

      {/* ── Recent Admin Activity ── */}
      {activity.length > 0 && (
        <div>
          <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 16, marginBottom: 12 }}>Recent Admin Activity</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {activity.map(entry => (
              <div key={entry.id} style={{
                background: 'var(--bg2)', borderRadius: 8, padding: '10px 14px',
                display: 'flex', gap: 10, alignItems: 'center', fontSize: 12,
              }}>
                <Badge color={ACTION_COLORS[entry.action] || 'var(--text2)'} >
                  {entry.action.replace(/_/g, ' ')}
                </Badge>
                <span style={{ flex: 1, color: 'var(--text2)' }}>
                  <strong>{entry.admin_name || 'Admin'}</strong>
                  {' — '}
                  {entry.target_type} {entry.target_id !== 'multiple' ? `#${entry.target_id}` : '(multiple)'}
                  {entry.reason ? ` · ${entry.reason.slice(0, 60)}` : ''}
                </span>
                <span style={{ color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                  {dateStr(entry.created_at)}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ── IP Bans Section ───────────────────────────────────────────────────────────

const IpBansSection = ({ toast }) => {
  const [bans,    setBans]    = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [pages,   setPages]   = useState(1);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addIp,     setAddIp]     = useState('');
  const [addType,   setAddType]   = useState('exact');
  const [addReason, setAddReason] = useState('');
  const [addExpiry, setAddExpiry] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const load = useCallback((p = 1) => {
    setLoading(true);
    Storage.adminGetIpBans(p)
      .then(r => { setBans(r.bans); setTotal(r.total); setPage(r.page); setPages(r.pages); setLoading(false); })
      .catch(e => { toast(e.message, 'error'); setLoading(false); });
  }, [toast]);

  useEffect(() => { load(1); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!addIp.trim() || !addReason.trim()) return;
    setAddLoading(true);
    try {
      await Storage.adminCreateIpBan(addIp.trim(), addType, addReason.trim(), addExpiry || null);
      toast('IP ban created.');
      setShowAdd(false); setAddIp(''); setAddReason(''); setAddExpiry('');
      load(1);
    } catch (err) { toast(err.message, 'error'); }
    setAddLoading(false);
  };

  const handleUnban = async (id) => {
    try {
      await Storage.adminDeleteIpBan(id);
      toast('IP ban removed.');
      load(page);
    } catch (err) { toast(err.message, 'error'); }
  };

  return (
    <div style={{ marginTop: 40 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <h3 style={{ fontFamily: 'var(--font-display)', fontSize: 18 }}>IP Bans <span style={{ fontSize: 13, color: 'var(--text3)', fontFamily: 'var(--font-body)' }}>({fmt(total)})</span></h3>
        <button className="btn-primary btn-sm" onClick={() => setShowAdd(s => !s)}>
          {showAdd ? 'Cancel' : '+ Add IP Ban'}
        </button>
      </div>

      {showAdd && (
        <form onSubmit={handleAdd} style={{ background: 'var(--bg2)', borderRadius: 'var(--radius)', padding: 16, marginBottom: 16, display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          <input value={addIp} onChange={e => setAddIp(e.target.value)} placeholder="IP address or prefix…" style={{ flex: '1 1 180px' }} required />
          <select value={addType} onChange={e => setAddType(e.target.value)} style={{ width: 'auto' }}>
            <option value="exact">Exact IP</option>
            <option value="prefix">Prefix (subnet)</option>
          </select>
          <input value={addReason} onChange={e => setAddReason(e.target.value)} placeholder="Reason (required)" style={{ flex: '1 1 200px' }} required />
          <input type="date" value={addExpiry} onChange={e => setAddExpiry(e.target.value)} style={{ width: 'auto' }} title="Expiry (leave blank for permanent)" />
          <button className="btn-primary btn-sm" type="submit" disabled={addLoading}>{addLoading ? 'Saving…' : 'Add Ban'}</button>
        </form>
      )}

      {loading && <p style={{ color: 'var(--text2)', fontSize: 13 }}>Loading…</p>}
      {!loading && bans.length === 0 && <p style={{ color: 'var(--text3)', fontSize: 13 }}>No active IP bans.</p>}

      {!loading && bans.length > 0 && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['IP Address', 'Type', 'Reason', 'Banned By', 'Created', 'Expiry', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text3)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bans.map(b => (
                <tr key={b.id} style={{ borderBottom: '1px solid var(--border2)' }}>
                  <td style={{ padding: '8px 10px', fontFamily: 'monospace' }}>{b.ip_address}</td>
                  <td style={{ padding: '8px 10px' }}><Badge color={b.ban_type === 'exact' ? 'var(--accent3)' : 'var(--danger)'}>{b.ban_type}</Badge></td>
                  <td style={{ padding: '8px 10px', color: 'var(--text2)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{b.reason}</td>
                  <td style={{ padding: '8px 10px', color: 'var(--text3)' }}>{b.banned_by_name || '—'}</td>
                  <td style={{ padding: '8px 10px', color: 'var(--text3)' }}>{dateStr(b.created_at)}</td>
                  <td style={{ padding: '8px 10px', color: 'var(--text3)' }}>{b.expires_at ? dateStr(b.expires_at) : 'Permanent'}</td>
                  <td style={{ padding: '8px 10px' }}>
                    <button className="btn-ghost btn-sm" style={{ fontSize: 12 }} onClick={() => handleUnban(b.id)}>Unban</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 12 }}>
          <button className="btn-ghost btn-sm" disabled={page <= 1} onClick={() => load(page - 1)}>← Prev</button>
          <span style={{ fontSize: 13, color: 'var(--text2)', alignSelf: 'center' }}>Page {page} / {pages}</span>
          <button className="btn-ghost btn-sm" disabled={page >= pages} onClick={() => load(page + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
};

// ── Users Tab ─────────────────────────────────────────────────────────────────

const UsersTab = ({ toast }) => {
  const [users,   setUsers]   = useState([]);
  const [total,   setTotal]   = useState(0);
  const [page,    setPage]    = useState(1);
  const [pages,   setPages]   = useState(1);
  const [search,  setSearch]  = useState('');
  const [filter,  setFilter]  = useState('all');
  const [loading, setLoading] = useState(false);

  const [selected,    setSelected]    = useState(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { type: 'ban'|'warn'|'export' }

  const [detailId,   setDetailId]  = useState(null);
  const [banLoading, setBanLoading] = useState(false);
  const [confirm,    setConfirm]   = useState(null);

  const load = useCallback((p = 1, s = search, f = filter) => {
    setLoading(true);
    setSelected(new Set());
    Storage.adminGetUsers({ page: p, search: s.trim() || undefined, filter: f })
      .then(r => { setUsers(r.users); setTotal(r.total); setPage(r.page); setPages(r.pages); setLoading(false); })
      .catch(e => { toast(e.message, 'error'); setLoading(false); });
  }, [search, filter, toast]);

  useEffect(() => { load(1); }, [filter]);

  const handleSearch = (e) => { e.preventDefault(); load(1, search, filter); };

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const allSelected = users.length > 0 && users.every(u => selected.has(u.id));
  const toggleAll   = () => setSelected(allSelected ? new Set() : new Set(users.map(u => u.id)));

  const executeBulk = async (reason) => {
    const ids = [...selected];
    setBulkLoading(true);
    try {
      if (bulkConfirm.type === 'ban') {
        const r = await Storage.adminBulkBanUsers(ids, reason);
        toast(`Banned ${r.succeeded} user(s).`);
      } else if (bulkConfirm.type === 'warn') {
        const r = await Storage.adminBulkWarnUsers(ids, reason);
        toast(`Warned ${r.succeeded} user(s).`);
      }
      setBulkConfirm(null);
      setSelected(new Set());
      load(page);
    } catch (e) { toast(e.message, 'error'); }
    setBulkLoading(false);
  };

  const handleExportSelected = () => {
    const sel = users.filter(u => selected.has(u.id));
    const csv = ['ID,Name,Email,Joined']
      .concat(sel.map(u => `${u.id},"${u.name.replace(/"/g, '""')}","${u.email}","${dateStr(u.createdAt)}"`))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href = url; a.download = 'chronos-users.csv'; a.click();
    URL.revokeObjectURL(url);
  };

  const openBanToggle = (u) => { setDetailId(null); setConfirm({ type: u.isBanned ? 'unban' : 'ban', user: u }); };

  const executeAction = async (reason) => {
    const { type, user: u } = confirm;
    setBanLoading(true);
    try {
      if      (type === 'ban')   await Storage.adminBanUser(u.id, reason);
      else if (type === 'unban') await Storage.adminUnbanUser(u.id);
      else if (type === 'warn')  await Storage.adminWarnUser(u.id, reason);
      const msg = type === 'ban' ? 'banned' : type === 'unban' ? 'unbanned' : 'warned';
      toast(`${u.name} has been ${msg}.`);
      setConfirm(null);
      if (type !== 'warn') load(page);
    } catch (e) { toast(e.message, 'error'); }
    setBanLoading(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: 8, flex: 1, minWidth: 200 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name or email…" style={{ flex: 1 }} />
          <button className="btn-primary btn-sm" type="submit">Search</button>
        </form>
        <select value={filter} onChange={e => { setFilter(e.target.value); setPage(1); }} style={{ width: 'auto' }}>
          <option value="all">All Users</option>
          <option value="admin">Admins</option>
          <option value="banned">Banned</option>
        </select>
        <span style={{ fontSize: 13, color: 'var(--text2)', alignSelf: 'center' }}>{fmt(total)} users</span>
      </div>

      {/* Bulk toolbar */}
      {selected.size > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '10px 16px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{selected.size} selected</span>
          <button className="btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => setBulkConfirm({ type: 'ban' })}>Bulk Ban</button>
          <button className="btn-ghost btn-sm" style={{ color: '#eab308' }} onClick={() => setBulkConfirm({ type: 'warn' })}>Bulk Warn</button>
          <button className="btn-ghost btn-sm" onClick={handleExportSelected}>Export CSV</button>
          <button className="btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {loading && <p style={{ color: 'var(--text2)' }}>Loading…</p>}

      {!loading && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '8px 10px', width: 32 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} title="Select all on this page" />
                </th>
                {['User', 'Email', 'Joined', 'Status', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text3)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map(u => (
                <tr key={u.id} style={{ borderBottom: '1px solid var(--border2)', background: selected.has(u.id) ? 'var(--bg2)' : '' }}
                  onMouseEnter={e => { if (!selected.has(u.id)) e.currentTarget.style.background = 'var(--bg2)'; }}
                  onMouseLeave={e => { if (!selected.has(u.id)) e.currentTarget.style.background = ''; }}>
                  <td style={{ padding: '10px 10px' }}>
                    <input type="checkbox" checked={selected.has(u.id)} onChange={() => toggleSelect(u.id)} />
                  </td>
                  <td style={{ padding: '10px 10px' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <Avatar user={u} size={28} />
                      <span style={{ fontWeight: 500 }}>{u.name}</span>
                    </div>
                  </td>
                  <td style={{ padding: '10px 10px', color: 'var(--text2)' }}>{u.email}</td>
                  <td style={{ padding: '10px 10px', color: 'var(--text3)' }}>{dateStr(u.createdAt)}</td>
                  <td style={{ padding: '10px 10px' }}>
                    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                      {u.isAdmin  && <Badge color="var(--accent3)">Admin</Badge>}
                      {u.isBanned && <Badge color="var(--danger)">Banned</Badge>}
                      {!u.isAdmin && !u.isBanned && <Badge color="var(--text3)">Active</Badge>}
                    </div>
                  </td>
                  <td style={{ padding: '10px 10px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-ghost btn-sm" onClick={() => setDetailId(u.id)} style={{ fontSize: 12 }}>View</button>
                      {!u.isAdmin && (
                        <>
                          <button className="btn-ghost btn-sm" onClick={() => setConfirm({ type: u.isBanned ? 'unban' : 'ban', user: u })}
                            style={{ fontSize: 12, color: u.isBanned ? 'var(--accent)' : 'var(--danger)' }}>
                            {u.isBanned ? 'Unban' : 'Ban'}
                          </button>
                          {!u.isBanned && (
                            <button className="btn-ghost btn-sm" onClick={() => setConfirm({ type: 'warn', user: u })} style={{ fontSize: 12, color: '#eab308' }}>Warn</button>
                          )}
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {users.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text3)', padding: '32px 0' }}>No users found.</p>}
        </div>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
          <button className="btn-ghost btn-sm" disabled={page <= 1} onClick={() => load(page - 1)}>← Prev</button>
          <span style={{ fontSize: 13, color: 'var(--text2)', alignSelf: 'center' }}>Page {page} / {pages}</span>
          <button className="btn-ghost btn-sm" disabled={page >= pages} onClick={() => load(page + 1)}>Next →</button>
        </div>
      )}

      {/* IP Bans section */}
      <IpBansSection toast={toast} />

      <UserDetailModal userId={detailId} onClose={() => setDetailId(null)} onBanToggle={openBanToggle} />

      {/* Single-user confirm */}
      <ConfirmModal
        open={!!confirm}
        title={confirm?.type === 'ban' ? `Ban ${confirm?.user?.name}?` : confirm?.type === 'unban' ? `Unban ${confirm?.user?.name}?` : `Warn ${confirm?.user?.name}?`}
        body={confirm?.type === 'ban' ? 'This will immediately log them out and prevent future logins.' : confirm?.type === 'unban' ? "This will restore the user's access to their account." : 'A formal warning will be logged. The user keeps full access.'}
        confirmLabel={confirm?.type === 'ban' ? 'Ban User' : confirm?.type === 'unban' ? 'Unban User' : 'Issue Warning'}
        danger={confirm?.type === 'ban'}
        requireReason loading={banLoading}
        onConfirm={executeAction} onClose={() => !banLoading && setConfirm(null)}
      />

      {/* Bulk action confirm */}
      <ConfirmModal
        open={!!bulkConfirm}
        title={bulkConfirm?.type === 'ban' ? `Bulk Ban ${selected.size} users?` : `Bulk Warn ${selected.size} users?`}
        body={bulkConfirm?.type === 'ban' ? 'All selected users will be banned immediately.' : 'A formal warning will be issued to all selected users.'}
        confirmLabel={bulkConfirm?.type === 'ban' ? 'Ban All' : 'Warn All'}
        danger={bulkConfirm?.type === 'ban'}
        requireReason loading={bulkLoading}
        onConfirm={executeBulk} onClose={() => !bulkLoading && setBulkConfirm(null)}
      />
    </div>
  );
};

// ── Content Tab ───────────────────────────────────────────────────────────────

// ── Bulk Delete Confirm (with "DELETE" typing for >10 items) ─────────────────

const BulkDeleteConfirm = ({ open, count, type, onConfirm, onClose, loading }) => {
  const [reason,      setReason]      = useState('');
  const [confirmation, setConfirmation] = useState('');
  const needsTyping = count > 10;

  useEffect(() => { if (!open) { setReason(''); setConfirmation(''); } }, [open]);
  if (!open) return null;

  const canSubmit = reason.trim() && (!needsTyping || confirmation === 'DELETE');

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 28, width: '100%', maxWidth: 440 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20, marginBottom: 12, color: 'var(--danger)' }}>
          Delete {count} {type}?
        </h2>
        <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
          This cannot be undone.{needsTyping && ' Type DELETE to confirm.'}
        </p>
        <div style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Reason <span style={{ color: 'var(--danger)' }}>*</span></label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="State your reason…" style={{ width: '100%', resize: 'vertical', boxSizing: 'border-box' }} />
        </div>
        {needsTyping && (
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: 'block', fontSize: 13, marginBottom: 6 }}>Type <strong>DELETE</strong> to confirm</label>
            <input value={confirmation} onChange={e => setConfirmation(e.target.value)} placeholder="DELETE" style={{ width: '100%', boxSizing: 'border-box' }} />
          </div>
        )}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={onClose} disabled={loading}>Cancel</button>
          <button className="btn-primary" onClick={() => onConfirm(reason)} disabled={loading || !canSubmit}
            style={{ background: 'var(--danger)', opacity: loading ? 0.7 : 1 }}>
            {loading ? 'Deleting…' : `Delete ${count} ${type}`}
          </button>
        </div>
      </div>
    </div>
  );
};

const TimelinesSection = ({ toast }) => {
  const [timelines,  setTimelines]  = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [pages,      setPages]      = useState(1);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(false);
  const [confirm,    setConfirm]    = useState(null);
  const [delLoading, setDelLoading] = useState(false);
  const [selected,   setSelected]   = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const load = useCallback((p = 1, s = search) => {
    setLoading(true);
    setSelected(new Set());
    Storage.adminGetTimelines({ page: p, search: s.trim() || undefined })
      .then(r => { setTimelines(r.timelines); setTotal(r.total); setPage(r.page); setPages(r.pages); setLoading(false); })
      .catch(e => { toast(e.message, 'error'); setLoading(false); });
  }, [search, toast]);

  useEffect(() => { load(1); }, []);

  const toggleSelect = (id) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const allSelected  = timelines.length > 0 && timelines.every(t => selected.has(t.id));
  const toggleAll    = () => setSelected(allSelected ? new Set() : new Set(timelines.map(t => t.id)));

  const executeDelete = async (reason) => {
    setDelLoading(true);
    try {
      await Storage.adminDeleteTimeline(confirm.timeline.id, reason);
      toast(`"${confirm.timeline.title}" deleted.`);
      setConfirm(null); load(page);
    } catch (e) { toast(e.message, 'error'); }
    setDelLoading(false);
  };

  const executeBulkDelete = async (reason) => {
    setDelLoading(true);
    try {
      const r = await Storage.adminBulkDeleteTimelines([...selected], reason);
      toast(`Deleted ${r.succeeded} timeline(s).`);
      setBulkConfirm(false); setSelected(new Set()); load(page);
    } catch (e) { toast(e.message, 'error'); }
    setDelLoading(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <form onSubmit={e => { e.preventDefault(); load(1, search); }} style={{ display: 'flex', gap: 8, flex: 1, minWidth: 200 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search timelines…" style={{ flex: 1 }} />
          <button className="btn-primary btn-sm" type="submit">Search</button>
        </form>
        <span style={{ fontSize: 13, color: 'var(--text2)', alignSelf: 'center' }}>{fmt(total)} timelines</span>
      </div>

      {selected.size > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 'var(--radius)', padding: '10px 16px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 13, fontWeight: 500 }}>{selected.size} selected</span>
          <button className="btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => setBulkConfirm(true)}>Bulk Delete</button>
          <button className="btn-ghost btn-sm" style={{ marginLeft: 'auto' }} onClick={() => setSelected(new Set())}>Clear</button>
        </div>
      )}

      {loading && <p style={{ color: 'var(--text2)' }}>Loading…</p>}
      {!loading && (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                <th style={{ padding: '8px 10px', width: 32 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                </th>
                {['Title', 'Author', 'Privacy', 'Events', 'Created', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text3)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {timelines.map(t => (
                <tr key={t.id} style={{ borderBottom: '1px solid var(--border2)', background: selected.has(t.id) ? 'var(--bg2)' : '' }}
                  onMouseEnter={e => { if (!selected.has(t.id)) e.currentTarget.style.background = 'var(--bg2)'; }}
                  onMouseLeave={e => { if (!selected.has(t.id)) e.currentTarget.style.background = ''; }}>
                  <td style={{ padding: '10px 10px' }}><input type="checkbox" checked={selected.has(t.id)} onChange={() => toggleSelect(t.id)} /></td>
                  <td style={{ padding: '10px 10px', maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    <span title={t.title} style={{ fontWeight: 500 }}>{t.title}</span>
                  </td>
                  <td style={{ padding: '10px 10px', color: 'var(--text2)' }}>{t.authorName || '—'}</td>
                  <td style={{ padding: '10px 10px' }}><Badge color={t.privacy === 'public' ? 'var(--accent)' : 'var(--text3)'}>{t.privacy}</Badge></td>
                  <td style={{ padding: '10px 10px', color: 'var(--text3)' }}>{t.eventCount ?? '—'}</td>
                  <td style={{ padding: '10px 10px', color: 'var(--text3)' }}>{dateStr(t.createdAt)}</td>
                  <td style={{ padding: '10px 10px' }}>
                    <button className="btn-ghost btn-sm" onClick={() => setConfirm({ timeline: t })} style={{ fontSize: 12, color: 'var(--danger)' }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {timelines.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text3)', padding: '32px 0' }}>No timelines found.</p>}
        </div>
      )}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          <button className="btn-ghost btn-sm" disabled={page <= 1} onClick={() => load(page - 1)}>← Prev</button>
          <span style={{ fontSize: 13, color: 'var(--text2)', alignSelf: 'center' }}>Page {page} / {pages}</span>
          <button className="btn-ghost btn-sm" disabled={page >= pages} onClick={() => load(page + 1)}>Next →</button>
        </div>
      )}
      <ConfirmModal open={!!confirm} title={`Delete "${confirm?.timeline?.title}"?`}
        body="This will permanently delete the timeline and all its comments, likes, and history. This cannot be undone."
        confirmLabel="Delete Timeline" danger requireReason loading={delLoading}
        onConfirm={executeDelete} onClose={() => !delLoading && setConfirm(null)} />
      <BulkDeleteConfirm open={bulkConfirm} count={selected.size} type="timelines"
        onConfirm={executeBulkDelete} onClose={() => !delLoading && setBulkConfirm(false)} loading={delLoading} />
    </div>
  );
};

const CommentsSection = ({ toast }) => {
  const [comments,   setComments]   = useState([]);
  const [total,      setTotal]      = useState(0);
  const [page,       setPage]       = useState(1);
  const [pages,      setPages]      = useState(1);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(false);
  const [confirm,    setConfirm]    = useState(null);
  const [delLoading, setDelLoading] = useState(false);
  const [selected,   setSelected]   = useState(new Set());
  const [bulkConfirm, setBulkConfirm] = useState(false);

  const load = useCallback((p = 1, s = search) => {
    setLoading(true);
    setSelected(new Set());
    Storage.adminGetComments({ page: p, search: s.trim() || undefined })
      .then(r => { setComments(r.comments); setTotal(r.total); setPage(r.page); setPages(r.pages); setLoading(false); })
      .catch(e => { toast(e.message, 'error'); setLoading(false); });
  }, [search, toast]);

  useEffect(() => { load(1); }, []);

  const toggleSelect = (id) => setSelected(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });

  const allSelected = comments.length > 0 && comments.every(c => selected.has(c.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(comments.map(c => c.id)));

  const executeDelete = async (reason) => {
    setDelLoading(true);
    try {
      await Storage.adminDeleteComment(confirm.comment.id, reason);
      toast(`${confirm.comment.type === 'reply' ? 'Reply' : 'Comment'} deleted.`);
      setConfirm(null);
      load(page);
    } catch (e) { toast(e.message, 'error'); }
    setDelLoading(false);
  };

  const executeBulkDelete = async (reason) => {
    setDelLoading(true);
    const ids = Array.from(selected);
    const commentIds = ids.filter(id => comments.find(c => c.id === id)?.type === 'comment');
    const replyIds   = ids.filter(id => comments.find(c => c.id === id)?.type === 'reply');
    try {
      await Storage.adminBulkDeleteComments(commentIds, replyIds, reason);
      toast(`${ids.length} item(s) deleted.`);
      setBulkConfirm(false);
      setSelected(new Set());
      load(page);
    } catch (e) { toast(e.message, 'error'); }
    setDelLoading(false);
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <form onSubmit={e => { e.preventDefault(); load(1, search); }} style={{ display: 'flex', gap: 8, flex: 1, minWidth: 200 }}>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search comments…" style={{ flex: 1 }} />
          <button className="btn-primary btn-sm" type="submit">Search</button>
        </form>
        <span style={{ fontSize: 13, color: 'var(--text2)', alignSelf: 'center' }}>{fmt(total)} total</span>
      </div>

      {selected.size > 0 && (
        <div style={{
          display: 'flex', gap: 10, alignItems: 'center', marginBottom: 12,
          background: 'var(--bg2)', borderRadius: 8, padding: '8px 14px',
          border: '1px solid var(--border)',
        }}>
          <span style={{ fontSize: 13, color: 'var(--text2)', flex: 1 }}>{selected.size} selected</span>
          <button
            className="btn-sm"
            onClick={() => setBulkConfirm(true)}
            style={{ background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', padding: '4px 12px' }}
          >
            Bulk Delete
          </button>
          <button className="btn-ghost btn-sm" onClick={() => setSelected(new Set())} style={{ fontSize: 12 }}>Clear</button>
        </div>
      )}

      {loading && <p style={{ color: 'var(--text2)' }}>Loading…</p>}
      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {comments.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingLeft: 4, marginBottom: 2 }}>
              <input type="checkbox" checked={allSelected} onChange={toggleAll} style={{ cursor: 'pointer' }} />
              <span style={{ fontSize: 12, color: 'var(--text3)' }}>Select all on page</span>
            </div>
          )}
          {comments.map(c => (
            <div key={c.id} style={{
              background: 'var(--bg2)', borderRadius: 8, padding: '12px 14px',
              display: 'flex', gap: 12, alignItems: 'flex-start',
              outline: selected.has(c.id) ? '2px solid var(--accent)' : 'none',
            }}>
              <input
                type="checkbox"
                checked={selected.has(c.id)}
                onChange={() => toggleSelect(c.id)}
                style={{ cursor: 'pointer', marginTop: 2, flexShrink: 0 }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
                  <span style={{ fontWeight: 500, fontSize: 13 }}>{c.userName}</span>
                  <Badge color={c.type === 'reply' ? 'var(--text3)' : 'var(--accent)'}>{c.type}</Badge>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>on "{c.timelineTitle}"</span>
                </div>
                <p style={{ fontSize: 13, color: 'var(--text2)', margin: 0, lineHeight: 1.5, wordBreak: 'break-word' }}>{c.text}</p>
                <span style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, display: 'block' }}>{dateStr(c.createdAt)}</span>
              </div>
              <button
                className="btn-ghost btn-sm"
                onClick={() => setConfirm({ comment: c })}
                style={{ fontSize: 12, color: 'var(--danger)', flexShrink: 0 }}
              >
                Delete
              </button>
            </div>
          ))}
          {comments.length === 0 && <p style={{ textAlign: 'center', color: 'var(--text3)', padding: '32px 0' }}>No comments found.</p>}
        </div>
      )}
      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 16 }}>
          <button className="btn-ghost btn-sm" disabled={page <= 1} onClick={() => load(page - 1)}>← Prev</button>
          <span style={{ fontSize: 13, color: 'var(--text2)', alignSelf: 'center' }}>Page {page} / {pages}</span>
          <button className="btn-ghost btn-sm" disabled={page >= pages} onClick={() => load(page + 1)}>Next →</button>
        </div>
      )}
      <ConfirmModal
        open={!!confirm}
        title={`Delete this ${confirm?.comment?.type}?`}
        body="This will permanently remove the comment and any replies to it."
        confirmLabel="Delete"
        danger requireReason={false}
        loading={delLoading}
        onConfirm={executeDelete}
        onClose={() => !delLoading && setConfirm(null)}
      />
      <BulkDeleteConfirm
        open={bulkConfirm}
        count={selected.size}
        label="comments/replies"
        loading={delLoading}
        onConfirm={executeBulkDelete}
        onClose={() => !delLoading && setBulkConfirm(false)}
      />
    </div>
  );
};

const ContentTab = ({ toast }) => {
  const [sub, setSub] = useState('timelines');
  const SUB = [{ id: 'timelines', label: 'Timelines' }, { id: 'comments', label: 'Comments & Replies' }];
  return (
    <div>
      <div style={{ display: 'flex', gap: 4, marginBottom: 20, borderBottom: '1px solid var(--border)', paddingBottom: 0 }}>
        {SUB.map(s => (
          <button key={s.id} onClick={() => setSub(s.id)} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: '10px 14px', fontSize: 13, fontWeight: sub === s.id ? 600 : 400,
            color: sub === s.id ? 'var(--accent)' : 'var(--text2)',
            borderBottom: sub === s.id ? '2px solid var(--accent)' : '2px solid transparent',
            marginBottom: -1,
          }}>
            {s.label}
          </button>
        ))}
      </div>
      {sub === 'timelines' && <TimelinesSection toast={toast} />}
      {sub === 'comments'  && <CommentsSection  toast={toast} />}
    </div>
  );
};

// ── Moderation Log Tab ────────────────────────────────────────────────────────

const LogTab = ({ toast }) => {
  const [log,          setLog]          = useState([]);
  const [total,        setTotal]        = useState(0);
  const [page,         setPage]         = useState(1);
  const [pages,        setPages]        = useState(1);
  const [action,       setAction]       = useState('');
  const [since,        setSince]        = useState('');
  const [loading,      setLoading]      = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const load = useCallback((p = 1) => {
    setLoading(true);
    Storage.adminGetLogs({ page: p, action: action || undefined, since: since || undefined })
      .then(r => { setLog(r.logs); setTotal(r.total); setPage(r.page); setPages(r.pages); setLoading(false); })
      .catch(e => { toast(e.message, 'error'); setLoading(false); });
  }, [action, since, toast]);

  useEffect(() => { load(1); }, [action, since]);

  const handleExport = async () => {
    setExportLoading(true);
    try {
      const csv = await Storage.adminExportModLog();
      const blob = new Blob([csv], { type: 'text/csv' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `mod-log-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast(e.message, 'error'); }
    setExportLoading(false);
  };

  const ACTION_COLORS = {
    ban_user:        'var(--danger)',
    unban_user:      'var(--accent)',
    delete_timeline: 'var(--danger)',
    delete_comment:  '#f97316',
    delete_reply:    '#f97316',
    warn_user:       '#eab308',
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <select value={action} onChange={e => setAction(e.target.value)} style={{ width: 'auto' }}>
          <option value="">All Actions</option>
          <option value="ban_user">Ban User</option>
          <option value="unban_user">Unban User</option>
          <option value="delete_timeline">Delete Timeline</option>
          <option value="delete_comment">Delete Comment</option>
          <option value="delete_reply">Delete Reply</option>
          <option value="warn_user">Warn User</option>
        </select>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <label style={{ fontSize: 13, color: 'var(--text2)', whiteSpace: 'nowrap' }}>Since</label>
          <input type="date" value={since} onChange={e => setSince(e.target.value)} style={{ width: 'auto' }} />
          {since && <button className="btn-ghost btn-sm" onClick={() => setSince('')} style={{ fontSize: 11 }}>Clear</button>}
        </div>
        <span style={{ fontSize: 13, color: 'var(--text2)', alignSelf: 'center', flex: 1 }}>{fmt(total)} entries</span>
        <button
          className="btn-ghost btn-sm"
          onClick={handleExport}
          disabled={exportLoading}
          style={{ fontSize: 12, whiteSpace: 'nowrap' }}
        >
          {exportLoading ? 'Exporting…' : 'Export CSV'}
        </button>
      </div>

      {loading && <p style={{ color: 'var(--text2)' }}>Loading…</p>}

      {!loading && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {log.map(entry => (
            <div key={entry.id} style={{
              background: 'var(--bg2)', borderRadius: 8, padding: '12px 16px',
              display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 13,
            }}>
              <Badge color={ACTION_COLORS[entry.action] || 'var(--text2)'}>
                {entry.action.replace(/_/g, ' ')}
              </Badge>
              <div style={{ flex: 1 }}>
                <div style={{ marginBottom: 2 }}>
                  <strong>{entry.adminName || 'Admin'}</strong>
                  {' → '}
                  <span style={{ color: 'var(--text2)' }}>{entry.targetType} #{entry.targetId}</span>
                </div>
                {entry.reason && <div style={{ color: 'var(--text2)', fontSize: 12 }}>{entry.reason}</div>}
              </div>
              <span style={{ color: 'var(--text3)', fontSize: 11, whiteSpace: 'nowrap' }}>{dateStr(entry.createdAt)}</span>
            </div>
          ))}

          {log.length === 0 && (
            <p style={{ textAlign: 'center', color: 'var(--text3)', padding: '32px 0' }}>No log entries found.</p>
          )}
        </div>
      )}

      {pages > 1 && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', marginTop: 20 }}>
          <button className="btn-ghost btn-sm" disabled={page <= 1} onClick={() => load(page - 1)}>← Prev</button>
          <span style={{ fontSize: 13, color: 'var(--text2)', alignSelf: 'center' }}>Page {page} / {pages}</span>
          <button className="btn-ghost btn-sm" disabled={page >= pages} onClick={() => load(page + 1)}>Next →</button>
        </div>
      )}
    </div>
  );
};

// ── Reports Tab ──────────────────────────────────────────────────────────────

const REPORT_REASONS = { spam: 'Spam', harassment: 'Harassment', misinformation: 'Misinformation', inappropriate: 'Inappropriate', copyright: 'Copyright', other: 'Other' };
const REPORT_STATUSES = ['pending', 'reviewed', 'actioned', 'dismissed'];
const TYPE_COLORS = { timeline: 'var(--accent)', comment: '#6b9fff', reply: '#b08fff', user: '#ff9f6b' };

const ReportsTab = ({ toast }) => {
  const [reports,        setReports]        = useState([]);
  const [total,          setTotal]          = useState(0);
  const [page,           setPage]           = useState(1);
  const [pages,          setPages]          = useState(1);
  const [loading,        setLoading]        = useState(true);
  const [statusFilter,   setStatusFilter]   = useState('pending');
  const [typeFilter,     setTypeFilter]     = useState('');
  const [selectedId,     setSelectedId]     = useState(null);
  const [detail,         setDetail]         = useState(null);
  const [detailLoading,  setDetailLoading]  = useState(false);
  const [actionNote,     setActionNote]     = useState('');
  const [actionLoading,  setActionLoading]  = useState(false);

  const load = useCallback(async (p = 1, sf = statusFilter, tf = typeFilter) => {
    setLoading(true);
    try {
      const data = await Storage.adminGetReports({ page: p, status: sf, contentType: tf });
      setReports(data.reports);
      setTotal(data.total);
      setPage(data.page);
      setPages(data.pages);
    } catch { toast('Failed to load reports.', 'error'); }
    finally { setLoading(false); }
  }, [statusFilter, typeFilter]);

  useEffect(() => { load(1); }, []);

  const loadDetail = async (id) => {
    setSelectedId(id);
    setDetail(null);
    setActionNote('');
    setDetailLoading(true);
    try {
      const data = await Storage.adminGetReport(id);
      setDetail(data);
    } catch { toast('Failed to load report detail.', 'error'); }
    finally { setDetailLoading(false); }
  };

  const doAction = async (status) => {
    if (!detail) return;
    setActionLoading(true);
    try {
      await Storage.adminUpdateReport(selectedId, { status, adminNote: actionNote.trim() || undefined });
      toast(`Report marked as ${status}.`);
      setDetail(null);
      setSelectedId(null);
      load(page);
    } catch (err) { toast(err.message, 'error'); }
    finally { setActionLoading(false); }
  };

  return (
    <div style={{ display: 'grid', gridTemplateColumns: selectedId ? '1fr 400px' : '1fr', gap: 24 }}>
      {/* ── Left: queue ── */}
      <div>
        {/* Filters */}
        <div style={{ display: 'flex', gap: 10, marginBottom: 20, flexWrap: 'wrap' }}>
          <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); load(1, e.target.value, typeFilter); }}
            style={{ padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 13 }}>
            <option value="">All Statuses</option>
            {REPORT_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
          </select>
          <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); load(1, statusFilter, e.target.value); }}
            style={{ padding: '6px 10px', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'var(--bg3)', color: 'var(--text)', fontSize: 13 }}>
            <option value="">All Types</option>
            {['timeline','comment','reply','user'].map(t => <option key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</option>)}
          </select>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: 'var(--text3)', alignSelf: 'center' }}>{total} report{total !== 1 ? 's' : ''}</span>
        </div>

        {loading ? <p style={{ color: 'var(--text3)', fontSize: 14 }}>Loading…</p> : reports.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: 'var(--text3)' }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>🏳️</div>
            <p style={{ fontSize: 15 }}>No reports found</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {reports.map(r => (
              <div key={r.id} onClick={() => loadDetail(r.id)}
                style={{
                  background: selectedId === r.id ? 'var(--surface2)' : 'var(--surface)',
                  border: `1px solid ${selectedId === r.id ? 'var(--accent)' : 'var(--border)'}`,
                  borderRadius: 'var(--radius)', padding: '12px 16px',
                  cursor: 'pointer', transition: 'border-color 0.15s',
                  display: 'grid', gridTemplateColumns: 'auto 1fr auto', gap: '8px 12px', alignItems: 'start',
                }}>
                <span style={{ background: TYPE_COLORS[r.content_type] + '22', color: TYPE_COLORS[r.content_type], borderRadius: 6, fontSize: 10, fontWeight: 700, padding: '2px 7px', whiteSpace: 'nowrap', marginTop: 2 }}>
                  {r.content_type?.toUpperCase()}
                </span>
                <div style={{ minWidth: 0 }}>
                  <p style={{ fontSize: 13, color: 'var(--text)', margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {r.is_escalated && <span title="Escalated" style={{ marginRight: 6 }}>🔥</span>}
                    {r.preview || '(no preview)'}
                  </p>
                  <p style={{ fontSize: 11, color: 'var(--text3)', margin: 0 }}>
                    {REPORT_REASONS[r.reason] ?? r.reason} · by {r.reporter_name}
                    {r.report_count > 1 && <span style={{ color: 'var(--danger)', marginLeft: 6 }}>⚑ {r.report_count} reports</span>}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: 11, color: 'var(--text3)' }}>{new Date(r.created_at).toLocaleDateString()}</span>
                  <br />
                  <span style={{ fontSize: 10, color: r.status === 'pending' ? 'var(--danger)' : 'var(--text3)', fontWeight: 600 }}>
                    {r.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}

        {pages > 1 && (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 12, marginTop: 20 }}>
            <button className="btn-ghost btn-sm" disabled={page <= 1} onClick={() => load(page - 1)}>← Prev</button>
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>Page {page} / {pages}</span>
            <button className="btn-ghost btn-sm" disabled={page >= pages} onClick={() => load(page + 1)}>Next →</button>
          </div>
        )}
      </div>

      {/* ── Right: detail panel ── */}
      {selectedId && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 20, maxHeight: '80vh', overflow: 'auto' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
            <h3 style={{ fontSize: 15, color: 'var(--text)', margin: 0 }}>Report #{selectedId}</h3>
            <button onClick={() => { setSelectedId(null); setDetail(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text3)', fontSize: 18 }}>✕</button>
          </div>

          {detailLoading ? <p style={{ color: 'var(--text3)', fontSize: 13 }}>Loading…</p> : detail && (
            <>
              {/* Reporter */}
              <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--bg3)', borderRadius: 'var(--radius)', fontSize: 13 }}>
                <p style={{ margin: '0 0 4px', color: 'var(--text2)' }}>Reported by <strong style={{ color: 'var(--text)' }}>{detail.report.reporter_name}</strong></p>
                <p style={{ margin: 0, color: 'var(--text2)' }}>
                  Reason: <strong style={{ color: 'var(--text)' }}>{REPORT_REASONS[detail.report.reason]}</strong>
                </p>
                {detail.report.details && <p style={{ margin: '4px 0 0', color: 'var(--text2)', fontStyle: 'italic' }}>"{detail.report.details}"</p>}
              </div>

              {/* Content preview */}
              {detail.content && (
                <div style={{ marginBottom: 16, padding: '10px 14px', background: 'var(--bg3)', borderRadius: 'var(--radius)', fontSize: 13 }}>
                  <p style={{ margin: '0 0 6px', fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Reported Content</p>
                  {detail.report.content_type === 'timeline' && (
                    <><p style={{ margin: '0 0 2px', fontWeight: 600, color: 'var(--text)' }}>{detail.content.title}</p>
                    <p style={{ margin: 0, color: 'var(--text2)' }}>by {detail.content.author?.name}</p></>
                  )}
                  {(detail.report.content_type === 'comment' || detail.report.content_type === 'reply') && (
                    <p style={{ margin: 0, color: 'var(--text)' }}>"{detail.content.text}"</p>
                  )}
                  {detail.report.content_type === 'user' && (
                    <><p style={{ margin: '0 0 2px', fontWeight: 600, color: 'var(--text)' }}>{detail.content.name}</p>
                    {detail.content.bio && <p style={{ margin: 0, color: 'var(--text2)' }}>{detail.content.bio}</p>}</>
                  )}
                </div>
              )}

              {/* All reports on this content */}
              {detail.history?.length > 1 && (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 8 }}>All reports on this content ({detail.history.length})</p>
                  {detail.history.map(h => (
                    <div key={h.id} style={{ fontSize: 12, color: 'var(--text2)', padding: '4px 0', borderBottom: '1px solid var(--border)' }}>
                      {h.reporter_name} — {REPORT_REASONS[h.reason]} ({h.status})
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              {detail.report.status === 'pending' && (
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                  <label style={{ fontSize: 12, color: 'var(--text2)', display: 'block', marginBottom: 6 }}>
                    Internal note (optional)
                  </label>
                  <textarea
                    value={actionNote}
                    onChange={e => setActionNote(e.target.value)}
                    placeholder="Note reason for action…"
                    rows={2}
                    style={{ width: '100%', fontSize: 12, resize: 'vertical', marginBottom: 12 }}
                  />
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                    <button className="btn-ghost btn-sm" disabled={actionLoading} onClick={() => doAction('dismissed')}>
                      Dismiss
                    </button>
                    <button className="btn-ghost btn-sm" disabled={actionLoading} onClick={() => doAction('reviewed')}
                      style={{ borderColor: 'rgba(201,169,110,0.4)', color: 'var(--accent)' }}>
                      Mark Reviewed
                    </button>
                    <button className="btn-sm" disabled={actionLoading} onClick={() => doAction('actioned')}
                      style={{ background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', padding: '6px 14px', fontSize: 12, fontWeight: 600 }}>
                      Action Taken
                    </button>
                  </div>
                </div>
              )}
              {detail.report.status !== 'pending' && (
                <p style={{ fontSize: 13, color: 'var(--text3)', marginTop: 12 }}>
                  Status: <strong style={{ color: 'var(--text2)' }}>{detail.report.status}</strong>
                  {detail.report.admin_note && <> — "{detail.report.admin_note}"</>}
                </p>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ── Template Builder ──────────────────────────────────────────────────────────

const ORIENTATIONS = [
  { value: 'horizontal', label: 'Horizontal' },
  { value: 'vertical',   label: 'Vertical'   },
  { value: 'zigzag',     label: 'Zigzag'     },
  { value: 'spine',      label: 'Spine'      },
  { value: 'newspaper',  label: 'Newspaper'  },
  { value: 'filmstrip',  label: 'Filmstrip'  },
  { value: 'storyline',  label: 'Storyline'  },
  { value: 'swimlane',   label: 'Swimlane'   },
  { value: 'tree',       label: 'Tree'       },
  { value: 'map',        label: 'Map'        },
];

const PALETTE = ['#FFB612','#3B82F6','#10B981','#EF4444','#8B5CF6','#F59E0B','#06B6D4','#EC4899','#84CC16','#F97316'];
const rndId = () => Math.random().toString(36).slice(2, 10);

const TemplateBuilder = ({ initial, onSave, onClose, toast }) => {
  const [name,      setName]      = useState(initial?.name        ?? '');
  const [desc,      setDesc]      = useState(initial?.description ?? '');
  const [category,  setCategory]  = useState(initial?.category    ?? '');
  const [orient,    setOrient]    = useState(initial?.orientation  ?? 'horizontal');
  const [color,     setColor]     = useState(initial?.previewColor ?? '#FFB612');
  const [tplColors, setTplColors] = useState({
    primary:   initial?.previewColor ?? '#FFB612',
    secondary: '#A8A9AD',
    tertiary:  '#0d0e0f',
    mode:      'dark',
    name:      '',
  });
  const [previewTplColor, setPreviewTplColor] = useState(null);
  const [events,    setEvents]    = useState(() => (initial?.timelineData?.events  ?? []).map(e => ({ ...e })));
  const [chapters,  setChapters]  = useState(() => (initial?.timelineData?.chapters ?? []).map(c => ({ ...c })));
  const [lanes,     setLanes]     = useState(() => (initial?.timelineData?.lanes    ?? initial?.timelineData?.swimlanes ?? []).map(l => ({ ...l })));
  const [editIdx,   setEditIdx]   = useState(null);
  const [saving,    setSaving]    = useState(false);
  const [errors,    setErrors]    = useState({});

  const addEvt = () => {
    const e = { id: rndId(), title: '', date: '', description: '', color: '' };
    if (orient === 'storyline' && chapters.length) e.chapterId = chapters[0].id;
    if (orient === 'swimlane'  && lanes.length)    e.laneId    = lanes[0].id;
    const nextIdx = events.length;
    setEvents(prev => [...prev, e]);
    setEditIdx(nextIdx);
  };
  const updateEvt   = (i, p) => setEvents(prev => prev.map((e, idx) => idx === i ? { ...e, ...p } : e));
  const deleteEvt   = (i) => { setEvents(prev => prev.filter((_, idx) => idx !== i)); setEditIdx(null); };
  const moveEvt     = (i, d) => setEvents(prev => { const n = [...prev]; const s = i + d; if (s < 0 || s >= n.length) return prev; [n[i], n[s]] = [n[s], n[i]]; return n; });

  const addChapter    = () => setChapters(prev => [...prev, { id: rndId(), title: `Chapter ${prev.length + 1}` }]);
  const updateChapter = (id, title) => setChapters(prev => prev.map(c => c.id === id ? { ...c, title } : c));
  const deleteChapter = (id) => { setChapters(prev => prev.filter(c => c.id !== id)); setEvents(prev => prev.map(e => e.chapterId === id ? { ...e, chapterId: '' } : e)); };

  const addLane    = () => setLanes(prev => [...prev, { id: rndId(), title: `Lane ${prev.length + 1}`, color: PALETTE[prev.length % PALETTE.length] }]);
  const updateLane = (id, p) => setLanes(prev => prev.map(l => l.id === id ? { ...l, ...p } : l));
  const deleteLane = (id) => { setLanes(prev => prev.filter(l => l.id !== id)); setEvents(prev => prev.map(e => e.laneId === id ? { ...e, laneId: '' } : e)); };

  const validate = () => {
    const e = {};
    if (!name.trim())      e.name     = 'Required';
    if (!desc.trim())      e.desc     = 'Required';
    if (!category)         e.category = 'Required';
    if (events.length < 3) e.events   = 'Need at least 3 events';
    events.forEach((ev, i) => { if (!ev.title.trim()) e[`evt_${i}`] = true; });
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      await onSave({
        name: name.trim(), description: desc.trim(), category,
        orientation: orient, previewColor: color,
        timelineData: {
          events,
          chapters: orient === 'storyline' ? chapters : [],
          lanes:    orient === 'swimlane'  ? lanes    : [],
        },
      });
    } catch (err) {
      toast(err.message || 'Failed to save.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const SectionBox = ({ children, style }) => (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '20px 24px', marginBottom: 20, ...style }}>
      {children}
    </div>
  );

  const SectionHead = ({ title, action }) => (
    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 14 }}>
      <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 15, flex: 1, margin: 0 }}>{title}</h2>
      {action}
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'var(--bg)', zIndex: 8000, overflowY: 'auto', fontFamily: 'var(--font-body)' }}>
      {/* Sticky header */}
      <div style={{ position: 'sticky', top: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 24px', display: 'flex', alignItems: 'center', gap: 12, height: 60, zIndex: 1 }}>
        <button className="btn-ghost btn-sm" onClick={onClose} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="back" size={14} /> Templates
        </button>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 18, flex: 1, margin: 0 }}>
          {initial ? `Edit: ${initial.name}` : 'Create New Template'}
        </h1>
        <div style={{ display: 'flex', gap: 8 }}>
          <TplThumb orientation={orient} color={previewTplColor ?? color} size="sm" />
          <button className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Saving…' : (initial ? 'Save Changes' : 'Create Template')}
          </button>
        </div>
      </div>

      <div style={{ maxWidth: 860, margin: '0 auto', padding: '28px 24px 48px' }}>

        {/* ── Metadata ── */}
        <SectionBox>
          <SectionHead title="Template Info" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label>Name {errors.name && <span style={{ color: 'var(--danger)', fontWeight: 400, fontSize: 12 }}>— {errors.name}</span>}</label>
              <input value={name} onChange={e => { setName(e.target.value); setErrors(p => ({ ...p, name: undefined })); }}
                placeholder="e.g. Wedding Planning Timeline"
                style={errors.name ? { borderColor: 'var(--danger)' } : {}} />
            </div>
            <div>
              <label>Category {errors.category && <span style={{ color: 'var(--danger)', fontWeight: 400, fontSize: 12 }}>— {errors.category}</span>}</label>
              <select value={category} onChange={e => { setCategory(e.target.value); setErrors(p => ({ ...p, category: undefined })); }}
                style={{ width: '100%', ...(errors.category ? { borderColor: 'var(--danger)' } : {}) }}>
                <option value="">Select category…</option>
                {TIMELINE_CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.emoji} {c.label}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <label>Description {errors.desc && <span style={{ color: 'var(--danger)', fontWeight: 400, fontSize: 12 }}>— {errors.desc}</span>}</label>
            <textarea value={desc} onChange={e => { setDesc(e.target.value); setErrors(p => ({ ...p, desc: undefined })); }}
              placeholder="Short description shown in the template browser…" rows={2} maxLength={200}
              style={{ resize: 'vertical', width: '100%', ...(errors.desc ? { borderColor: 'var(--danger)' } : {}) }} />
            <div style={{ fontSize: 11, color: 'var(--text3)', textAlign: 'right', marginTop: 2 }}>{desc.length}/200</div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginTop: 14 }}>
            <div>
              <label>Layout</label>
              <select value={orient} onChange={e => setOrient(e.target.value)} style={{ width: '100%' }}>
                {ORIENTATIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
            <div>
              <label>Preview Color</label>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                <div style={{
                  width: 28, height: 28, borderRadius: 6, flexShrink: 0,
                  background: previewTplColor ?? color,
                  border: '1px solid var(--border2)',
                }} />
                <span style={{ fontSize: 12, color: 'var(--text3)' }}>Set in Color Theme ↓</span>
              </div>
            </div>
          </div>
        </SectionBox>

        {/* ── Color Theme ── */}
        <SectionBox>
          <SectionHead title="Color Theme" />
          <ColorCustomizer
            colors={tplColors}
            onChange={(c) => {
              const next = c.name ? c : { ...tplColors, ...c };
              setTplColors(next);
              setColor(next.primary);
              setPreviewTplColor(null);
            }}
            onPreviewColor={(c) => setPreviewTplColor(c.primary)}
            onClearPreview={() => setPreviewTplColor(null)}
          />
        </SectionBox>

        {/* ── Chapters (storyline only) ── */}
        {orient === 'storyline' && (
          <SectionBox>
            <SectionHead title="Chapters"
              action={<button className="btn-ghost btn-sm" onClick={addChapter} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Icon name="add" size={13} /> Add Chapter</button>} />
            {chapters.length === 0
              ? <p style={{ color: 'var(--text3)', fontSize: 13, margin: 0 }}>No chapters yet. Add chapters to group events in a Storyline layout.</p>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {chapters.map(ch => (
                    <div key={ch.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input value={ch.title} onChange={e => updateChapter(ch.id, e.target.value)} placeholder="Chapter title…" style={{ flex: 1 }} />
                      <button className="btn-ghost btn-sm" onClick={() => deleteChapter(ch.id)} style={{ color: 'var(--danger)', flexShrink: 0 }}>Delete</button>
                    </div>
                  ))}
                </div>
            }
          </SectionBox>
        )}

        {/* ── Lanes (swimlane only) ── */}
        {orient === 'swimlane' && (
          <SectionBox>
            <SectionHead title="Lanes"
              action={<button className="btn-ghost btn-sm" onClick={addLane} style={{ display: 'flex', alignItems: 'center', gap: 5 }}><Icon name="add" size={13} /> Add Lane</button>} />
            {lanes.length === 0
              ? <p style={{ color: 'var(--text3)', fontSize: 13, margin: 0 }}>No lanes yet. Add lanes for a Swimlane layout.</p>
              : <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {lanes.map(lane => (
                    <div key={lane.id} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <input value={lane.title} onChange={e => updateLane(lane.id, { title: e.target.value })} placeholder="Lane title…" style={{ flex: 1 }} />
                      <input type="color" value={lane.color || '#FFB612'} onChange={e => updateLane(lane.id, { color: e.target.value })}
                        style={{ width: 36, height: 34, border: '1px solid var(--border2)', borderRadius: 'var(--radius)', cursor: 'pointer', padding: '2px', flexShrink: 0 }} />
                      <button className="btn-ghost btn-sm" onClick={() => deleteLane(lane.id)} style={{ color: 'var(--danger)', flexShrink: 0 }}>Delete</button>
                    </div>
                  ))}
                </div>
            }
          </SectionBox>
        )}

        {/* ── Events ── */}
        <SectionBox>
          <SectionHead title="Events"
            action={
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 12, color: errors.events ? 'var(--danger)' : 'var(--text3)' }}>
                  {events.length} event{events.length !== 1 ? 's' : ''}{errors.events ? ' — need ≥3' : ' (min 3)'}
                </span>
                <button className="btn-primary btn-sm" onClick={addEvt} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <Icon name="add" size={13} /> Add Event
                </button>
              </div>
            }
          />

          {events.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--text3)' }}>
              <div style={{ fontSize: 36, marginBottom: 10 }}>📅</div>
              <p style={{ fontSize: 13 }}>No events yet. Click "Add Event" to get started.</p>
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {events.map((ev, idx) => {
                const isOpen  = editIdx === idx;
                const hasErr  = !!errors[`evt_${idx}`];
                return (
                  <div key={ev.id} style={{ border: `1px solid ${isOpen ? 'var(--accent)' : hasErr ? 'var(--danger)' : 'var(--border2)'}`, borderRadius: 'var(--radius)', overflow: 'hidden', transition: 'border-color 0.15s' }}>
                    {/* Collapsed row */}
                    <div
                      style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', cursor: 'pointer', userSelect: 'none',
                        background: isOpen ? 'color-mix(in srgb, var(--accent) 5%, var(--surface))' : 'var(--surface)',
                      }}
                      onClick={() => setEditIdx(isOpen ? null : idx)}
                    >
                      <span style={{ fontSize: 11, color: 'var(--text3)', minWidth: 22, textAlign: 'right', flexShrink: 0 }}>{idx + 1}</span>
                      <span style={{ flex: 1, fontSize: 13, fontWeight: 500, color: ev.title ? 'var(--text)' : 'var(--text3)', overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis' }}>
                        {ev.title || <em>Untitled event</em>}
                      </span>
                      {ev.date && <span style={{ fontSize: 11, color: 'var(--text3)', flexShrink: 0 }}>{ev.date}</span>}
                      {hasErr && <span style={{ fontSize: 10, color: 'var(--danger)', fontWeight: 700, flexShrink: 0 }}>Title required</span>}
                      <div style={{ display: 'flex', gap: 2, flexShrink: 0 }} onClick={e => e.stopPropagation()}>
                        <button type="button" onClick={() => moveEvt(idx, -1)} disabled={idx === 0} className="btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 12 }} aria-label="Move up">↑</button>
                        <button type="button" onClick={() => moveEvt(idx, 1)} disabled={idx === events.length - 1} className="btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 12 }} aria-label="Move down">↓</button>
                        <button type="button" onClick={() => deleteEvt(idx)} className="btn-ghost btn-sm" style={{ padding: '2px 6px', fontSize: 12, color: 'var(--danger)' }} aria-label="Delete event">✕</button>
                      </div>
                    </div>

                    {/* Expanded editor */}
                    {isOpen && (
                      <div style={{ padding: '14px 14px 12px', borderTop: '1px solid var(--border2)', background: 'var(--bg)', display: 'flex', flexDirection: 'column', gap: 10 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                          <div>
                            <label style={{ fontSize: 11, color: hasErr ? 'var(--danger)' : 'var(--text3)', marginBottom: 4, display: 'block' }}>Title *</label>
                            <input value={ev.title} autoFocus
                              onChange={e => { updateEvt(idx, { title: e.target.value }); setErrors(p => ({ ...p, [`evt_${idx}`]: undefined })); }}
                              placeholder="e.g. [Your event title]"
                              style={hasErr ? { borderColor: 'var(--danger)' } : {}} />
                          </div>
                          <div>
                            <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4, display: 'block' }}>Date (text)</label>
                            <input value={ev.date ?? ''} onChange={e => updateEvt(idx, { date: e.target.value })} placeholder="e.g. 2024-01-01 or [Month Year]" />
                          </div>
                        </div>
                        <div>
                          <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4, display: 'block' }}>Description</label>
                          <textarea value={ev.description ?? ''} onChange={e => updateEvt(idx, { description: e.target.value })}
                            placeholder="Details. Use [bracketed text] as placeholder prompts." rows={2} style={{ resize: 'vertical', width: '100%' }} />
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
                          {orient === 'storyline' && chapters.length > 0 && (
                            <div style={{ flex: '1 1 150px' }}>
                              <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4, display: 'block' }}>Chapter</label>
                              <select value={ev.chapterId ?? ''} onChange={e => updateEvt(idx, { chapterId: e.target.value })} style={{ width: '100%' }}>
                                <option value="">No chapter</option>
                                {chapters.map(ch => <option key={ch.id} value={ch.id}>{ch.title}</option>)}
                              </select>
                            </div>
                          )}
                          {orient === 'swimlane' && lanes.length > 0 && (
                            <div style={{ flex: '1 1 150px' }}>
                              <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4, display: 'block' }}>Lane</label>
                              <select value={ev.laneId ?? ''} onChange={e => updateEvt(idx, { laneId: e.target.value })} style={{ width: '100%' }}>
                                <option value="">No lane</option>
                                {lanes.map(l => <option key={l.id} value={l.id}>{l.title}</option>)}
                              </select>
                            </div>
                          )}
                          {orient === 'tree' && events.length > 1 && (
                            <div style={{ flex: '1 1 150px' }}>
                              <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4, display: 'block' }}>Parent Event</label>
                              <select value={ev.parentId ?? ''} onChange={e => updateEvt(idx, { parentId: e.target.value })} style={{ width: '100%' }}>
                                <option value="">No parent (root)</option>
                                {events.filter((_, i) => i !== idx).map(e2 => <option key={e2.id} value={e2.id}>{e2.title || `Event ${events.indexOf(e2) + 1}`}</option>)}
                              </select>
                            </div>
                          )}
                          <div>
                            <label style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 4, display: 'block' }}>Event Color</label>
                            <div style={{ display: 'flex', gap: 5, alignItems: 'center', flexWrap: 'wrap' }}>
                              <button type="button" onClick={() => updateEvt(idx, { color: '' })} aria-label="Default color" aria-pressed={!ev.color}
                                style={{ width: 22, height: 22, borderRadius: '50%', background: 'var(--bg2)', border: `2px solid ${!ev.color ? 'var(--text)' : 'var(--border2)'}`, cursor: 'pointer', padding: 0, fontSize: 10, color: 'var(--text3)' }}>✕</button>
                              {PALETTE.map(c => (
                                <button key={c} type="button" onClick={() => updateEvt(idx, { color: c })} aria-label={c} aria-pressed={ev.color === c}
                                  style={{ width: 22, height: 22, borderRadius: '50%', background: c, border: `2px solid ${ev.color === c ? 'var(--text)' : 'transparent'}`, cursor: 'pointer', padding: 0 }} />
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </SectionBox>

        <p style={{ fontSize: 12, color: 'var(--text3)', textAlign: 'center', marginBottom: 0 }}>
          Tip: use <strong>[bracketed text]</strong> in titles and descriptions as fill-in prompts for users.
        </p>
      </div>
    </div>
  );
};

// ── Templates Tab ─────────────────────────────────────────────────────────────

const TemplatesTab = ({ toast }) => {
  const [templates,    setTemplates]    = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [confirm,      setConfirm]      = useState(null); // { type, id, name }
  const [actionLoading,setActionLoading]= useState(false);
  const [builder,      setBuilder]      = useState(null);  // null | 'create' | full-template-object

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { templates: list } = await Storage.adminGetTemplates();
      setTemplates(list);
    } catch { toast('Failed to load templates.', 'error'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, []);

  const handleOpenEdit = async (tpl) => {
    try {
      const full = await Storage.adminGetTemplateFull(tpl.id);
      setBuilder(full);
    } catch (err) { toast(err.message || 'Failed to load template.', 'error'); }
  };

  const handleBuilderSave = async (payload) => {
    if (builder === 'create') {
      await Storage.adminCreateTemplate(payload);
      toast('Template created.');
    } else {
      await Storage.adminUpdateTemplate(builder.id, payload);
      toast('Template updated.');
    }
    setBuilder(null);
    load();
  };

  const handleToggleFeatured = async (tpl) => {
    const next = !tpl.isFeatured;
    setTemplates(prev => prev.map(t => t.id === tpl.id ? { ...t, isFeatured: next } : t));
    try {
      await Storage.adminToggleTemplateFeatured(tpl.id, next);
      toast(`${tpl.name} ${next ? 'marked as featured' : 'unfeatured'}.`);
    } catch (err) {
      setTemplates(prev => prev.map(t => t.id === tpl.id ? { ...t, isFeatured: tpl.isFeatured } : t));
      toast(err.message, 'error');
    }
  };

  const handleDuplicate = async (tpl) => {
    try {
      const { id, name } = await Storage.adminDuplicateTemplate(tpl.id);
      toast(`Duplicated as "${name}".`);
      load();
    } catch (err) { toast(err.message, 'error'); }
  };

  const handleDelete = async () => {
    if (!confirm) return;
    setActionLoading(true);
    try {
      await Storage.adminDeleteTemplate(confirm.id);
      toast(`"${confirm.name}" deleted.`);
      setConfirm(null);
      load();
    } catch (err) { toast(err.message, 'error'); }
    finally { setActionLoading(false); }
  };

  const maxUses = Math.max(1, ...templates.map(t => t.useCount));

  return (
    <div>
      {/* Builder overlay */}
      {builder && (
        <TemplateBuilder
          initial={builder === 'create' ? null : builder}
          onSave={handleBuilderSave}
          onClose={() => setBuilder(null)}
          toast={toast}
        />
      )}

      {/* Header */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 20, alignItems: 'center' }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, flex: 1 }}>Timeline Templates</h2>
        <span style={{ fontSize: 13, color: 'var(--text3)' }}>{templates.length} templates</span>
        <button className="btn-primary btn-sm" onClick={() => setBuilder('create')} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="add" size={13} /> New Template
        </button>
      </div>

      {/* Use-count chart */}
      {templates.filter(t => t.useCount > 0).length > 0 && (
        <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: '16px 20px', marginBottom: 24 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 12 }}>Template Popularity</div>
          <div className="tpl-bar-chart">
            {[...templates].sort((a, b) => b.useCount - a.useCount).slice(0, 10).map(t => (
              <div key={t.id} className="tpl-bar-row">
                <div className="tpl-bar-label" title={t.name}>{t.name}</div>
                <div className="tpl-bar-track">
                  <div className="tpl-bar-fill" style={{ width: `${(t.useCount / maxUses) * 100}%` }} />
                </div>
                <div className="tpl-bar-count">{t.useCount}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Table */}
      {loading ? <p style={{ color: 'var(--text2)' }}>Loading…</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ borderBottom: '1px solid var(--border)' }}>
                {['Template', 'Category', 'Layout', 'Uses', 'Featured', 'Created', ''].map(h => (
                  <th key={h} style={{ textAlign: 'left', padding: '8px 10px', color: 'var(--text3)', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {templates.map(tpl => (
                <tr key={tpl.id} style={{ borderBottom: '1px solid var(--border2)' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--bg2)'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}
                >
                  <td style={{ padding: '10px 10px' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                      <div style={{ width: 36, height: 36, borderRadius: 6, overflow: 'hidden', flexShrink: 0 }}>
                        <TplThumb orientation={tpl.orientation} color={tpl.previewColor} size="sm" />
                      </div>
                      <div>
                        <div style={{ fontWeight: 600 }}>{tpl.name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 1 }}>{tpl.description?.slice(0, 60)}{tpl.description?.length > 60 ? '…' : ''}</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '10px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                    {TIMELINE_CATEGORIES.find(c => c.value === tpl.category)?.label ?? tpl.category}
                  </td>
                  <td style={{ padding: '10px 10px', color: 'var(--text2)', whiteSpace: 'nowrap' }}>
                    {tpl.orientation.charAt(0).toUpperCase() + tpl.orientation.slice(1)}
                  </td>
                  <td style={{ padding: '10px 10px', color: 'var(--text2)', textAlign: 'center' }}>{tpl.useCount}</td>
                  <td style={{ padding: '10px 10px' }}>
                    <button
                      type="button"
                      onClick={() => handleToggleFeatured(tpl)}
                      title={tpl.isFeatured ? 'Remove featured status' : 'Mark as featured'}
                      style={{
                        background: tpl.isFeatured ? 'color-mix(in srgb, #f59e0b 20%, var(--surface))' : 'var(--bg2)',
                        border: `1px solid ${tpl.isFeatured ? '#f59e0b' : 'var(--border2)'}`,
                        borderRadius: 6, padding: '3px 8px', cursor: 'pointer',
                        fontSize: 12, color: tpl.isFeatured ? '#b45309' : 'var(--text3)',
                        fontWeight: tpl.isFeatured ? 600 : 400,
                      }}
                    >
                      {tpl.isFeatured ? '⭐ Featured' : '☆ Feature'}
                    </button>
                  </td>
                  <td style={{ padding: '10px 10px', color: 'var(--text3)', whiteSpace: 'nowrap' }}>
                    {new Date(tpl.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </td>
                  <td style={{ padding: '10px 10px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button className="btn-ghost btn-sm" onClick={() => handleOpenEdit(tpl)} style={{ fontSize: 11 }}>Edit</button>
                      <button className="btn-ghost btn-sm" onClick={() => handleDuplicate(tpl)} style={{ fontSize: 11 }}>Copy</button>
                      <button
                        className="btn-ghost btn-sm"
                        onClick={() => setConfirm({ type: 'delete', id: tpl.id, name: tpl.name })}
                        style={{ fontSize: 11, color: 'var(--danger)' }}
                      >Delete</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {templates.length === 0 && (
            <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text3)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
              <p>No templates yet. Run <code>add_templates.sql</code> to seed the initial set.</p>
            </div>
          )}
        </div>
      )}

      {/* Delete confirm */}
      {confirm?.type === 'delete' && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
          <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', padding: 28, width: '100%', maxWidth: 400 }}>
            <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, marginBottom: 12 }}>Delete Template?</h2>
            <p style={{ color: 'var(--text2)', fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>
              Delete <strong>"{confirm.name}"</strong>? This will not affect timelines already created from it.
            </p>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => setConfirm(null)} disabled={actionLoading}>Cancel</button>
              <button
                className="btn-primary"
                onClick={handleDelete}
                disabled={actionLoading}
                style={{ background: 'var(--danger)', opacity: actionLoading ? 0.7 : 1 }}
              >
                {actionLoading ? 'Deleting…' : 'Delete Template'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ── Dev Blog Tab ──────────────────────────────────────────────────────────────

const EMPTY_FORM = { title: '', body: '', published: false, cover_image: null };

const DevBlogTab = ({ toast }) => {
  const [posts,      setPosts]      = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [editing,    setEditing]    = useState(null); // null=list, 'new'=new, obj=edit
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [coverFile,  setCoverFile]  = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);

  const load = () => {
    setLoading(true);
    Storage.getAdminDevBlogPosts()
      .then(({ posts }) => setPosts(posts))
      .catch(() => toast('Failed to load posts', 'error'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setCoverFile(null);
    setCoverPreview(null);
    setEditing('new');
  };

  const openEdit = (post) => {
    setForm({ title: post.title, body: post.body, published: !!post.published, cover_image: post.cover_image });
    setCoverFile(null);
    setCoverPreview(post.cover_image || null);
    setEditing(post);
  };

  const handleCoverPick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverFile(file);
    setCoverPreview(URL.createObjectURL(file));
  };

  const handleSave = async () => {
    if (!form.title.trim()) return toast('Title is required', 'error');
    if (!form.body.trim())  return toast('Body is required', 'error');
    setSaving(true);
    try {
      let coverUrl = form.cover_image;
      if (coverFile) {
        const { url } = await Storage.uploadDevBlogCover(coverFile);
        coverUrl = url;
      }
      const data = { ...form, cover_image: coverUrl };
      if (editing === 'new') {
        await Storage.createDevBlogPost(data);
        toast('Post created');
      } else {
        await Storage.updateDevBlogPost(editing.id, data);
        toast('Post updated');
      }
      setEditing(null);
      load();
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await Storage.deleteDevBlogPost(id);
      toast('Post deleted');
      setPosts(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      toast(err.message, 'error');
    } finally {
      setConfirmDel(null);
    }
  };

  const handleTogglePublish = async (post) => {
    try {
      const { published } = await Storage.toggleDevBlogPublish(post.id);
      setPosts(prev => prev.map(p => p.id === post.id ? { ...p, published: published ? 1 : 0 } : p));
      toast(published ? 'Post published' : 'Post unpublished');
    } catch (err) {
      toast(err.message, 'error');
    }
  };

  if (editing) {
    return (
      <div style={{ maxWidth: 720 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
          <button className="btn-ghost btn-sm" onClick={() => setEditing(null)} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
            <Icon name="back" size={14} /> All Posts
          </button>
          <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 18, flex: 1 }}>
            {editing === 'new' ? 'New Post' : 'Edit Post'}
          </h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>Title</label>
            <input
              value={form.title}
              onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
              maxLength={200}
              placeholder="Post title…"
              style={{ width: '100%', padding: '10px 12px', borderRadius: 'var(--radius)', border: '1px solid var(--border2)', background: 'var(--surface)', color: 'var(--text)', fontSize: 15, boxSizing: 'border-box' }}
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>Body</label>
            <RichTextEditor
              value={form.body}
              onChange={body => setForm(f => ({ ...f, body }))}
              maxLength={20000}
              placeholder="Write your dev blog post…"
            />
          </div>

          <div>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: 'var(--text2)', marginBottom: 6 }}>Cover Image</label>
            <div style={{ position: 'relative', display: 'inline-block' }}>
              <div style={{
                padding: '10px 16px', border: '2px dashed var(--border2)',
                borderRadius: 'var(--radius)', cursor: 'pointer',
                color: 'var(--text2)', fontSize: 13, display: 'inline-block',
              }}>
                {coverPreview ? 'Change image' : 'Upload cover image'}
              </div>
              <input type="file" accept="image/*" onChange={handleCoverPick}
                style={{ position: 'absolute', inset: 0, opacity: 0, cursor: 'pointer', width: '100%', height: '100%' }} />
            </div>
            {coverPreview && (
              <div style={{ marginTop: 10 }}>
                <img src={coverPreview} alt="" style={{ height: 160, borderRadius: 'var(--radius)', objectFit: 'cover', border: '1px solid var(--border)' }} />
                <button
                  className="btn-ghost btn-sm"
                  style={{ display: 'block', marginTop: 6, color: 'var(--danger)', fontSize: 12 }}
                  onClick={() => { setCoverFile(null); setCoverPreview(null); setForm(f => ({ ...f, cover_image: null })); }}
                >
                  Remove image
                </button>
              </div>
            )}
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={form.published}
              onChange={e => setForm(f => ({ ...f, published: e.target.checked }))}
            />
            <span style={{ fontSize: 14, color: 'var(--text)' }}>Publish immediately</span>
          </label>

          <div style={{ display: 'flex', gap: 12 }}>
            <button className="btn-primary" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : editing === 'new' ? 'Create Post' : 'Save Changes'}
            </button>
            <button className="btn-ghost" onClick={() => setEditing(null)}>Cancel</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: 20 }}>Dev Blog Posts</h2>
        <button className="btn-primary btn-sm" onClick={openNew}>+ New Post</button>
      </div>

      {loading ? (
        <p style={{ color: 'var(--text3)', fontSize: 14 }}>Loading…</p>
      ) : !posts.length ? (
        <p style={{ color: 'var(--text3)', fontSize: 14 }}>No posts yet. Create your first one!</p>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {posts.map(post => (
            <div key={post.id} style={{
              background: 'var(--surface)', border: '1px solid var(--border)',
              borderRadius: 'var(--radius-xl)', padding: '16px 20px',
              display: 'flex', alignItems: 'center', gap: 16,
            }}>
              {post.cover_image && (
                <img src={post.cover_image} alt="" style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 'var(--radius)', flexShrink: 0 }} />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {post.title}
                  </span>
                  <span style={{
                    fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 10,
                    background: post.published ? 'rgba(91,184,91,0.18)' : 'var(--bg3)',
                    color: post.published ? '#5cb85c' : 'var(--text3)',
                    flexShrink: 0,
                  }}>
                    {post.published ? 'Published' : 'Draft'}
                  </span>
                </div>
                <p style={{ fontSize: 12, color: 'var(--text3)', margin: 0 }}>
                  {new Date(post.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                </p>
              </div>
              <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                <button className="btn-ghost btn-sm" onClick={() => handleTogglePublish(post)}>
                  {post.published ? 'Unpublish' : 'Publish'}
                </button>
                <button className="btn-ghost btn-sm" onClick={() => openEdit(post)}>Edit</button>
                {confirmDel === post.id ? (
                  <>
                    <button className="btn-sm" style={{ background: 'var(--danger)', color: '#fff', border: 'none', borderRadius: 'var(--radius)', padding: '4px 10px', cursor: 'pointer', fontSize: 12 }} onClick={() => handleDelete(post.id)}>Confirm</button>
                    <button className="btn-ghost btn-sm" onClick={() => setConfirmDel(null)}>Cancel</button>
                  </>
                ) : (
                  <button className="btn-ghost btn-sm" style={{ color: 'var(--danger)' }} onClick={() => setConfirmDel(post.id)}>Delete</button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── Feedback Tab ──────────────────────────────────────────────────────────────

const FEEDBACK_TYPES = [
  { value: '',                label: 'All types'    },
  { value: 'bug',             label: 'Bug'          },
  { value: 'feature_request', label: 'Feature'      },
  { value: 'general',         label: 'General'      },
  { value: 'complaint',       label: 'Complaint'    },
  { value: 'praise',          label: 'Praise'       },
];
const FEEDBACK_STATUS = [
  { value: '',          label: 'All status' },
  { value: 'new',       label: 'New'        },
  { value: 'read',      label: 'Read'       },
  { value: 'actioned',  label: 'Actioned'   },
  { value: 'dismissed', label: 'Dismissed'  },
];
const FEEDBACK_STATUS_NEXT = ['read', 'actioned', 'dismissed'];

const FeedbackTab = ({ toast }) => {
  const [items,    setItems]    = useState([]);
  const [total,    setTotal]    = useState(0);
  const [unread,   setUnread]   = useState(0);
  const [loading,  setLoading]  = useState(false);
  const [type,     setType]     = useState('');
  const [status,   setStatus]   = useState('');
  const [q,        setQ]        = useState('');
  const [expanded, setExpanded] = useState(new Set());
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [noteDraft, setNoteDraft] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const { feedback, total: t, unread: u } = await Storage.adminListFeedback({ type, status, q, limit: 100 });
      setItems(feedback);
      setTotal(t);
      setUnread(u);
    } catch (err) {
      toast({ message: err.message || 'Failed to load feedback', type: 'error' });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [type, status]);

  const toggleExpand = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const updateStatus = async (id, newStatus) => {
    try {
      await Storage.adminUpdateFeedback(id, { status: newStatus });
      setItems(items.map(x => x.id === id ? { ...x, status: newStatus } : x));
      if (newStatus !== 'new') setUnread(u => Math.max(0, u - (items.find(x => x.id === id)?.status === 'new' ? 1 : 0)));
    } catch (err) {
      toast({ message: err.message || 'Failed to update', type: 'error' });
    }
  };
  const saveNote = async (id) => {
    try {
      await Storage.adminUpdateFeedback(id, { adminNote: noteDraft });
      setItems(items.map(x => x.id === id ? { ...x, adminNote: noteDraft } : x));
      setEditingNoteId(null);
      setNoteDraft('');
    } catch (err) {
      toast({ message: err.message || 'Failed to save note', type: 'error' });
    }
  };
  const deleteEntry = async (id) => {
    if (!window.confirm('Delete this feedback entry?')) return;
    try {
      await Storage.adminDeleteFeedback(id);
      setItems(items.filter(x => x.id !== id));
      setTotal(t => t - 1);
    } catch (err) {
      toast({ message: err.message || 'Failed to delete', type: 'error' });
    }
  };
  const markAllRead = async () => {
    if (!window.confirm('Mark every new feedback entry as read?')) return;
    try {
      await Storage.adminMarkAllFeedbackRead();
      setItems(items.map(x => x.status === 'new' ? { ...x, status: 'read' } : x));
      setUnread(0);
    } catch (err) {
      toast({ message: err.message || 'Failed to mark all read', type: 'error' });
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Feedback</h2>
        {unread > 0 && (
          <span style={{
            background: 'var(--accent)', color: '#1A1408',
            padding: '2px 9px', fontSize: 11, fontWeight: 700, borderRadius: 10,
          }}>{unread} new</span>
        )}
        <span style={{ fontSize: 12, color: 'var(--text3)' }}>{total} total</span>
        <button className="btn-secondary btn-sm" onClick={markAllRead} style={{ marginLeft: 'auto' }} disabled={unread === 0}>
          Mark all read
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <select value={type}   onChange={e => setType(e.target.value)}   style={{ fontSize: 13, padding: '6px 10px' }}>
          {FEEDBACK_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
        </select>
        <select value={status} onChange={e => setStatus(e.target.value)} style={{ fontSize: 13, padding: '6px 10px' }}>
          {FEEDBACK_STATUS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
        </select>
        <input
          type="text"
          value={q}
          onChange={e => setQ(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') load(); }}
          placeholder="Search subject + body…"
          style={{ fontSize: 13, padding: '6px 10px', flex: 1, minWidth: 200 }}
        />
        <button className="btn-secondary btn-sm" onClick={load} disabled={loading}>Search</button>
      </div>
      {loading && <p style={{ color: 'var(--text3)' }}>Loading…</p>}
      {!loading && items.length === 0 && (
        <p style={{ color: 'var(--text3)' }}>No feedback matches the filters.</p>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {items.map(item => {
          const isExpanded = expanded.has(item.id);
          const isNew = item.status === 'new';
          return (
            <div
              key={item.id}
              style={{
                background: 'var(--surface)',
                border: '1px solid var(--border)',
                borderLeft: isNew ? '3px solid var(--accent, #C9A96E)' : '1px solid var(--border)',
                borderRadius: 6, padding: '10px 14px',
              }}
            >
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer' }}
                onClick={() => toggleExpand(item.id)}
              >
                <span style={{ fontSize: 11, color: 'var(--text3)', minWidth: 90 }}>
                  {new Date(item.createdAt).toLocaleDateString()}
                </span>
                <span style={{
                  fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.04em',
                  background: 'var(--bg3)', borderRadius: 4, padding: '1px 6px',
                  color: 'var(--text2)', minWidth: 70, textAlign: 'center',
                }}>{item.type.replace('_', ' ')}</span>
                <span style={{ fontSize: 13, fontWeight: 600, flex: 1, color: 'var(--text)' }}>
                  {item.subject}
                </span>
                <span style={{ fontSize: 11, color: 'var(--text3)' }}>
                  {item.userName ? `${item.userName}${item.userEmail ? ` (${item.userEmail})` : ''}` : 'Anonymous'}
                </span>
                <span style={{
                  fontSize: 10, textTransform: 'uppercase',
                  padding: '2px 7px', borderRadius: 4,
                  background: isNew ? 'rgba(201, 169, 110, 0.18)' : 'var(--bg3)',
                  color: isNew ? 'var(--accent, #C9A96E)' : 'var(--text3)',
                  fontWeight: 600,
                }}>{item.status}</span>
              </div>
              {isExpanded && (
                <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)' }}>
                  <p style={{ whiteSpace: 'pre-wrap', fontSize: 13, color: 'var(--text2)', lineHeight: 1.5, margin: 0 }}>
                    {item.body}
                  </p>
                  {item.adminNote && editingNoteId !== item.id && (
                    <p style={{ marginTop: 10, padding: '8px 10px', background: 'var(--bg3)', borderRadius: 4, fontSize: 12, color: 'var(--text2)' }}>
                      <strong style={{ color: 'var(--accent)' }}>Admin note:</strong> {item.adminNote}
                    </p>
                  )}
                  {editingNoteId === item.id && (
                    <div style={{ marginTop: 10 }}>
                      <textarea
                        value={noteDraft}
                        onChange={e => setNoteDraft(e.target.value)}
                        placeholder="Internal admin note…"
                        rows={3}
                        style={{ width: '100%', fontSize: 12, padding: 6 }}
                      />
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end', marginTop: 6 }}>
                        <button className="btn-secondary btn-sm" onClick={() => { setEditingNoteId(null); setNoteDraft(''); }}>Cancel</button>
                        <button className="btn-primary btn-sm" onClick={() => saveNote(item.id)}>Save note</button>
                      </div>
                    </div>
                  )}
                  <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {FEEDBACK_STATUS_NEXT.map(s => (
                      <button
                        key={s}
                        className="btn-secondary btn-sm"
                        onClick={() => updateStatus(item.id, s)}
                        disabled={item.status === s}
                        style={{ fontSize: 11 }}
                      >
                        Mark {s}
                      </button>
                    ))}
                    {editingNoteId !== item.id && (
                      <button
                        className="btn-secondary btn-sm"
                        onClick={() => { setEditingNoteId(item.id); setNoteDraft(item.adminNote || ''); }}
                        style={{ fontSize: 11 }}
                      >
                        {item.adminNote ? 'Edit note' : 'Add note'}
                      </button>
                    )}
                    <button className="btn-danger btn-sm" onClick={() => deleteEntry(item.id)} style={{ fontSize: 11, marginLeft: 'auto' }}>
                      Delete
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const TABS = [
  { id: 'stats',     label: 'Stats'     },
  { id: 'users',     label: 'Users'     },
  { id: 'content',   label: 'Content'   },
  { id: 'reports',   label: 'Reports'   },
  { id: 'feedback',  label: 'Feedback'  },
  { id: 'log',       label: 'Mod Log'   },
  { id: 'templates', label: 'Templates' },
  { id: 'devblog',   label: 'Dev Blog'  },
];

const AdminPanel = ({ user, onBack, theme, onThemeToggle }) => {
  const [tab, setTab] = useState('stats');
  const [pendingReportCount, setPendingReportCount] = useState(0);
  const { toasts, add: addToast, remove: removeToast } = useToasts();
  const isMobile = useIsMobile();

  useEffect(() => {
    Storage.adminGetReportPendingCount()
      .then(({ count }) => setPendingReportCount(count))
      .catch(() => {});
  }, []);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg)', fontFamily: 'var(--font-body)' }}>

      {/* Nav */}
      <nav style={{
        background: 'var(--surface)', borderBottom: '1px solid var(--border)',
        padding: '0 24px', display: 'flex', alignItems: 'center', gap: 16, height: 64,
      }}>
        <button className="btn-ghost btn-sm" onClick={onBack} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Icon name="back" size={14} /> Dashboard
        </button>
        <div style={{ fontSize: 22, marginLeft: 4 }} aria-hidden="true">🛡</div>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 20, color: 'var(--accent)', flex: 1 }}>Admin Panel</h1>
        {isMobile ? (
          <MobileMenu>
            {(close) => (
              <>
                {TABS.map(t => (
                  <button
                    key={t.id}
                    onClick={() => { setTab(t.id); if (t.id === 'reports') setPendingReportCount(0); close(); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                      padding: '12px 16px', background: 'none', border: 'none', cursor: 'pointer',
                      fontSize: 14, fontWeight: tab === t.id ? 600 : 400,
                      color: tab === t.id ? 'var(--accent)' : 'var(--text)',
                      borderBottom: '1px solid var(--border)',
                    }}
                  >
                    {t.label}
                    {t.id === 'reports' && pendingReportCount > 0 && (
                      <span style={{
                        background: 'var(--danger)', color: '#fff', borderRadius: 10,
                        fontSize: 10, fontWeight: 700, padding: '1px 6px', minWidth: 18, textAlign: 'center',
                      }}>
                        {pendingReportCount > 99 ? '99+' : pendingReportCount}
                      </span>
                    )}
                  </button>
                ))}
              </>
            )}
          </MobileMenu>
        ) : (
          <span style={{ fontSize: 13, color: 'var(--text3)' }}>{user.name}</span>
        )}
      </nav>

      {/* Tab bar — desktop only */}
      {!isMobile && (
        <div style={{ background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '0 24px', display: 'flex', gap: 4 }}>
          {TABS.map(t => (
            <button
              key={t.id}
              onClick={() => { setTab(t.id); if (t.id === 'reports') setPendingReportCount(0); }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '14px 16px', fontSize: 14, fontWeight: tab === t.id ? 600 : 400,
                color: tab === t.id ? 'var(--accent)' : 'var(--text2)',
                borderBottom: tab === t.id ? '2px solid var(--accent)' : '2px solid transparent',
                transition: 'color 0.15s',
                display: 'flex', alignItems: 'center', gap: 6,
              }}
            >
              {t.label}
              {t.id === 'reports' && pendingReportCount > 0 && (
                <span style={{
                  background: 'var(--danger)', color: '#fff', borderRadius: 10,
                  fontSize: 10, fontWeight: 700, padding: '1px 6px', minWidth: 18, textAlign: 'center',
                }}>
                  {pendingReportCount > 99 ? '99+' : pendingReportCount}
                </span>
              )}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      <main style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
        {tab === 'stats'     && <StatsTab />}
        {tab === 'users'     && <UsersTab     toast={addToast} />}
        {tab === 'content'   && <ContentTab   toast={addToast} />}
        {tab === 'reports'   && <ReportsTab   toast={addToast} />}
        {tab === 'feedback'  && <FeedbackTab  toast={addToast} />}
        {tab === 'log'       && <LogTab       toast={addToast} />}
        {tab === 'templates' && <TemplatesTab toast={addToast} />}
        {tab === 'devblog'   && <DevBlogTab   toast={addToast} />}
      </main>

      <Toast toasts={toasts} remove={removeToast} />
    </div>
  );
};

export default AdminPanel;
